import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { memberProfileAPI, groupPlayAPI } from '../../services/api';
import { GroupPlayInstance, MemberProfile } from '../../types';
import { formatTimeString, formatDate } from '../../utils/dateUtils';

interface RegisterMemberToGroupPlayDialogProps {
  open: boolean;
  onClose: () => void;
  groupPlayInstance: GroupPlayInstance | null;
  groupPlayId: string;
  onSuccess: () => void;
}

const RegisterMemberToGroupPlayDialog: React.FC<RegisterMemberToGroupPlayDialogProps> = ({
  open,
  onClose,
  groupPlayInstance,
  groupPlayId,
  onSuccess
}) => {
  // Fetch active member profiles
  const { profiles: memberProfiles, loading: loadingMembers, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ autoLoad: false });
  
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      refetchMemberProfiles();
    }
  }, [open]);



  const handleRegister = async () => {
    if (!selectedMember || !groupPlayInstance) return;

    try {
      setLoading(true);
      setError(null);

      await groupPlayAPI.registerMemberToGroupPlay(groupPlayId, groupPlayInstance.id, {
        memberProfileId: selectedMember.id,
        notes
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error registering member to group play:', error);
      setError(error.response?.data?.message || 'Failed to register member');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedMember(null);
    setNotes('');
    setError(null);
    onClose();
  };

  if (!groupPlayInstance) return null;

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? handleClose : undefined}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 1 }} />
          Register Member to Group Play
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Group Play Instance Info */}
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{groupPlayInstance.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(groupPlayInstance.sessionDate)} • {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
          </Typography>
          {groupPlayInstance.venueNames && groupPlayInstance.venueNames.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Venues: {groupPlayInstance.venueNames.join(', ')}
            </Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${groupPlayInstance.confirmedCount || 0}/${groupPlayInstance.capacity} confirmed`}
              size="small"
              color={((groupPlayInstance.confirmedCount || 0) / groupPlayInstance.capacity) >= 0.8 ? 'warning' : 'default'}
            />
            {groupPlayInstance.isWaitlistAvailable && (groupPlayInstance.waitlistCount || 0) > 0 && (
              <Chip 
                label={`${groupPlayInstance.waitlistCount} on waitlist`}
                size="small"
                color="warning"
              />
            )}
            <Chip 
              label={groupPlayInstance.status}
              size="small"
              color="primary"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Member Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            Select Member
          </Typography>
          
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              value={selectedMember}
              onChange={(_, newValue) => setSelectedMember(newValue)}
              options={memberProfiles}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              getOptionKey={(option) => option.id}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email} • {typeof option.membershipTier === 'object' ? option.membershipTier?.name : option.membershipTier || 'Standard'}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search members by name or email..."
                  variant="outlined"
                />
              )}
              fullWidth
              disabled={loading}
            />
          )}
        </Box>

        {/* Notes */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Notes (Optional)
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Add any notes about this registration..."
            fullWidth
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRegister}
          variant="contained"
          disabled={!selectedMember || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Registering...' : 'Register Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterMemberToGroupPlayDialog;
